/**
 * Admin Normalizer
 * Transforms admin API responses (modules, dictionaries) to frontend models.
 * @module api/normalizers/admin
 */

// LLM:METADATA
// :hierarchy: [Domain:Admin | Data_Normalization | Service]
// :relates-to: uses: "responseNormalizer.js", required-by: "admin.client.js"
// :rationale: "Transforms backend admin responses (modules list, dictionaries) to match frontend expectations with consistent field naming and structure"
// :contract: pre: "validated data from admin contract", post: "normalized admin response for useAppInit/useCommonDicts composables", invariant: "always returns expected structure for Vuex consumers"
// :decision_cache: "Keep `/api/modules` shape compatible with Vuex (`{ modules, menuItems }`) while tolerating legacy array payloads [ADMIN-NORM-002]"
// LLM:END

/**
 * Normalize module item from API response
 * Transforms backend module structure to frontend model
 *
 * @param {Object} module - Raw module data from API
 * @param {string} module.id - Module ID (string from PHP mysqli)
 * @param {string} module.name - Module name
 * @param {string} [module.table_name] - Database table name
 * @param {number} [module.active] - Active status (0/1)
 * @param {string} [module.description] - Module description
 * @param {string} [module.icon] - Module icon
 * @param {number} [module.order_num] - Display order
 * @returns {Object} Normalized module object
 * @returns {number} returns.id - Module ID (converted to number)
 * @returns {string} returns.name - Module name
 * @returns {string} [returns.tableName] - Database table name
 * @returns {boolean} returns.active - Active status
 * @returns {string} [returns.description] - Module description
 * @returns {string} [returns.icon] - Module icon
 * @returns {number} [returns.orderNum] - Display order
 */
export function normalizeModule(module) {
  if (!module || typeof module !== "object") {
    return null
  }

  const resolvedId = module.module_id ?? module.id ?? null
  const resolvedName = module.module_name ?? module.name ?? ""

  return {
    ...module,
    module_id: resolvedId != null ? Number(resolvedId) : null,
    id:
      module.id != null
        ? Number(module.id)
        : resolvedId != null
          ? Number(resolvedId)
          : null,
    module_name: resolvedName,
    name: module.name ?? resolvedName,
    tableName: module.table_name ?? null,
    active: module.active === 1 || module.active === true,
    description: module.description ?? null,
    icon: module.icon ?? null,
    orderNum: module.order_num != null ? Number(module.order_num) : null,
  }
}

/**
 * Normalize one menu item so pack-boundary consumers compare canonical ids.
 *
 * @precondition item is a backend `/api/modules` menu node or nullish.
 * @postcondition Recursively normalizes nested `subItems`/`moreItems` arrays and converts finite `pack_id` values to numbers while preserving unrelated menu metadata.
 * @mutates none
 */
export function normalizeMenuItem(item) {
  if (!item || typeof item !== "object") {
    return null
  }

  const normalized = {
    ...item,
    subItems: Array.isArray(item.subItems)
      ? item.subItems.map(normalizeMenuItem).filter(Boolean)
      : [],
  }

  if (Array.isArray(item.moreItems)) {
    normalized.moreItems = item.moreItems.map(normalizeMenuItem).filter(Boolean)
  }

  if (
    item.pack_id !== undefined &&
    item.pack_id !== null &&
    item.pack_id !== ""
  ) {
    const numericPackId = Number(item.pack_id)
    normalized.pack_id = Number.isFinite(numericPackId)
      ? numericPackId
      : item.pack_id
  }

  return normalized
}

/**
 * Normalize modules list response
 * Transforms backend modules array to frontend model structure
 *
 * @param {Object|Array} response - Raw modules response from API
 * @returns {{modules: Array, menuItems: Array}} Normalized modules payload
 */
export function normalizeModulesList(response) {
  if (Array.isArray(response)) {
    return {
      modules: response.map(normalizeModule).filter(Boolean),
      menuItems: [],
    }
  }

  if (!response || typeof response !== "object") {
    return {
      modules: [],
      menuItems: [],
    }
  }

  const modules = Array.isArray(response.modules) ? response.modules : []
  const menuItems = Array.isArray(response.menuItems) ? response.menuItems : []

  return {
    ...response,
    modules: modules.map(normalizeModule).filter(Boolean),
    menuItems: menuItems.map(normalizeMenuItem).filter(Boolean),
  }
}

/**
 * Normalize dictionary item from API response
 * Transforms backend dict item structure to frontend model
 *
 * @param {Object} item - Raw dictionary item from API
 * @param {string} item.id - Item ID (string from PHP mysqli)
 * @param {string} item.title - Item title (backend always sends title, not name per strict contract)
 * @param {string} [item.titleForPanel] - Panel-specific title (for delivery/payment types)
 * @param {string} [item.label] - Display label
 * @param {*} [item.value] - Item value
 * @param {number} [item.active] - Active status (0/1/null)
 * @returns {Object} Normalized dictionary item
 * @returns {number} returns.id - Item ID (converted to number)
 * @returns {string} returns.name - Item name (normalized from title)
 * @returns {string} returns.title - Item title (backward compatible alias)
 * @returns {string} [returns.titleForPanel] - Panel-specific title (preserved if present)
 * @returns {string} returns.label - Display label
 * @returns {*} returns.value - Item value
 * @returns {boolean} returns.active - Active status
 */
export function normalizeDictItem(item) {
  if (!item || typeof item !== "object") {
    return null
  }

  // Strict contract: backend sends 'title', not 'name'
  // Normalizer converts to frontend model with 'name' field
  const resolvedName = item.title ?? item.name ?? ""
  const resolvedLabel = item.label ?? item.title ?? item.name ?? ""

  const normalized = {
    id: item.id != null ? Number(item.id) : null, // Convert string ID to number
    name: resolvedName,
    title: resolvedName, // Backward compatible alias
    label: resolvedLabel,
    value: item.value ?? null,
    active: item.active === 1 || item.active === true || item.active === null,
  }

  // Preserve titleForPanel if present (for delivery_options, payment_types)
  // LLM:METADATA
  // :topology: [Domain:Admin | Data_Normalization | FieldPreservation]
  // :relates-to: used-by: "OrderRow.vue" (lines 112, 152-153, 161, 602), used-by: "deliveryTypeById", used-by: "paymentTypesFiltered"
  // :rationale: "Backend provides titleForPanel for delivery_options (m_item_title_in_panel) and payment_types (m_item_editor_title) to display compact labels in order rows. Must preserve this field through normalization to prevent 'undefined' display values."
  // :contract: pre: "item may contain titleForPanel", post: "normalized item retains titleForPanel if present", invariant: "titleForPanel is never stripped during normalization"
  // :decision_cache: "Fixed BUGS-019/BUGS-020 regression where titleForPanel was lost, causing undefined delivery/payment labels [DICT-NORM-001]"
  // LLM:END
  if (item.titleForPanel != null) {
    normalized.titleForPanel = item.titleForPanel
  }

  return normalized
}

/**
 * Normalize dictionary response
 * Transforms backend dictionary array to frontend model structure
 *
 * @param {Array} dict - Raw dictionary array from API
 * @returns {Array} Normalized dictionary items array
 */
export function normalizeDict(dict) {
  if (!Array.isArray(dict)) {
    return []
  }

  return dict.map(normalizeDictItem).filter(Boolean) // Remove any null entries
}
